import { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_RULES } from "@marshmallow-duel/simulation";

import { AbilitiesDialog } from "./components/AbilitiesDialog";
import { GameScreen } from "./components/GameScreen";
import { MapEditor } from "./components/MapEditor";
import { MapSelection } from "./components/MapSelection";
import { OnlineExperience } from "./components/OnlineExperience";
import { OriginalDialog } from "./components/OriginalDialog";
import { OriginalMenu } from "./components/OriginalMenu";
import { PlayerEditor } from "./components/PlayerEditor";
import { ScaledStage } from "./components/ScaledStage";
import { SettingsDialog } from "./components/SettingsDialog";
import { ApiError, publishMap } from "./game/api-client";
import { localProfilesForMatch, localizeDefaultFighterNames, normalizeFighterSelection, normalizeLocalFighters } from "./game/local-roster";
import { DEFAULT_GAME_PREFERENCES, normalizeGamePreferences } from "./game/preferences";
import { arrivedFromSplatch } from "./game/splatch-session";
import { translate } from "./i18n";

import type { ArenaMap, Language, MatchRules, PlayerSlot } from "@marshmallow-duel/simulation";
import type { MenuAction } from "./components/OriginalMenu";
import type { LocalFighter } from "./game/local-roster";

type Screen = "mode" | "menu" | "game" | "online" | "editor";
type Dialog = "maps" | "playersSelect" | "playersEdit" | "hall" | "controls" | "abilities" | "settings" | "authors" | null;

const readStored = <T,>(key: string, fallback: T): T => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as T : fallback;
  } catch {
    return fallback;
  }
};

export const App = () => {
  const [language, setLanguage] = useState<Language>(() => readStored("mdr.language", "cz"));
  const [screen, setScreen] = useState<Screen>(() => arrivedFromSplatch ? "online" : "mode");
  const [dialog, setDialog] = useState<Dialog>(null);
  const [rules, setRules] = useState<MatchRules>(() => readStored("mdr.rules", { ...DEFAULT_RULES, abilityIds: [...DEFAULT_RULES.abilityIds] }));
  const [preferences, setPreferences] = useState(() => normalizeGamePreferences(readStored("mdr.preferences", DEFAULT_GAME_PREFERENCES)));
  const [fighters, setFighters] = useState<LocalFighter[]>(() => normalizeLocalFighters(readStored("mdr.fighters", readStored("mdr.profiles", null))));
  const [fighterSelection, setFighterSelection] = useState<string[]>(() => normalizeFighterSelection(readStored("mdr.activeFighters", null), fighters));
  const [selectedMap, setSelectedMap] = useState<ArenaMap | null>(null);
  const [gameReturn, setGameReturn] = useState<Screen>("menu");
  const t = useMemo(() => (key: Parameters<typeof translate>[1]) => translate(language, key), [language]);
  const profiles = useMemo(() => localProfilesForMatch(fighters, fighterSelection), [fighters, fighterSelection]);

  useEffect(() => localStorage.setItem("mdr.language", JSON.stringify(language)), [language]);
  useEffect(() => localStorage.setItem("mdr.rules", JSON.stringify(rules)), [rules]);
  useEffect(() => localStorage.setItem("mdr.preferences", JSON.stringify(preferences)), [preferences]);
  useEffect(() => localStorage.setItem("mdr.fighters", JSON.stringify(fighters)), [fighters]);
  useEffect(() => localStorage.setItem("mdr.activeFighters", JSON.stringify(fighterSelection)), [fighterSelection]);

  useEffect(() => {
    if (!preferences.music || !["mode", "menu", "editor"].includes(screen)) return;
    const music = new Audio("/assets/original/music/menu.mp3");
    music.loop = true;
    music.volume = 0.42;
    const play = () => void music.play().catch(() => undefined);
    play();
    window.addEventListener("pointerdown", play, { once: true });
    return () => {
      window.removeEventListener("pointerdown", play);
      music.pause();
      music.currentTime = 0;
    };
  }, [preferences.music, screen]);

  const toggleLanguage = () => {
    const next = language === "cz" ? "en" : "cz";
    setFighters((current) => localizeDefaultFighterNames(current, next));
    setLanguage(next);
  };

  const menuAction = (action: MenuAction) => {
    switch (action) {
      case "begin": setDialog("maps"); break;
      case "loadPlayers": setDialog("playersSelect"); break;
      case "editPlayers": setDialog("playersEdit"); break;
      case "hall": setDialog("hall"); break;
      case "editor": setScreen("editor"); break;
      case "controls": setDialog("controls"); break;
      case "abilities": setDialog("abilities"); break;
      case "settings": setDialog("settings"); break;
      case "authors": setDialog("authors"); break;
      case "exit": setScreen("mode"); break;
    }
  };

  const selectMap = (map: ArenaMap) => {
    setSelectedMap(map);
    setGameReturn("menu");
    setDialog(null);
    setScreen("game");
  };

  const recordLocalWinner = useCallback((winner: PlayerSlot) => {
    const winnerId = profiles[winner].id;
    setFighters((current) => current.map((fighter) => fighter.id === winnerId ? { ...fighter, score: fighter.score + 1 } : fighter));
  }, [profiles]);

  if (screen === "mode") {
    return (
      <ScaledStage className="mode-stage">
        <img className="mode-title-image" src="/assets/original/backgrounds/bg_menu_title.png" alt="Marshmallow Duel Reloaded" />
        <section className="mode-panel">
          <h1>{t("mode.title")}</h1>
          <button type="button" onClick={() => setScreen("menu")}><strong>{t("mode.local")}</strong><span>{t("mode.localHint")}</span></button>
          <button type="button" onClick={() => setScreen("online")}><strong>{t("mode.online")}</strong><span>{t("mode.onlineHint")}</span></button>
        </section>
        <button type="button" className="language-button" onClick={toggleLanguage}>{language === "cz" ? "Čeština" : "English"}</button>
        <div className="menu-water" aria-hidden="true" />
      </ScaledStage>
    );
  }

  if (screen === "game" && selectedMap) {
    return <GameScreen language={language} map={selectedMap} rules={{ ...rules, playerCount: selectedMap.playerCount }} profiles={profiles} preferences={preferences} t={t} onLeave={() => setScreen(gameReturn)} onSelectMap={gameReturn === "menu" ? () => { setScreen("menu"); setDialog("maps"); } : undefined} onMatchWinner={gameReturn === "menu" ? recordLocalWinner : undefined} />;
  }

  if (screen === "online") {
    return <OnlineExperience language={language} preferences={preferences} t={t} onBack={() => setScreen("mode")} />;
  }

  if (screen === "editor") {
    return <MapEditor language={language} t={t} onBack={() => setScreen("menu")} onTest={(map) => { setSelectedMap(map); setGameReturn("editor"); setScreen("game"); }} onPublish={async (map, description) => {
      try {
        await publishMap(map, description, language);
      } catch (error) {
        if (error instanceof ApiError && error.code === "unauthorized") throw new Error(t("editor.splatchRequired"));
        throw new Error(t("editor.publishFailed"));
      }
    }} />;
  }

  return (
    <>
      <OriginalMenu language={language} t={t} onAction={menuAction} onToggleLanguage={toggleLanguage} />
      {dialog === "maps" && <MapSelection language={language} playerCount={rules.playerCount} t={t} onSelect={selectMap} onClose={() => setDialog(null)} />}
      {(dialog === "playersSelect" || dialog === "playersEdit") && <PlayerEditor mode={dialog === "playersSelect" ? "select" : "edit"} playerCount={rules.playerCount} fighters={fighters} selection={fighterSelection} t={t} onFightersChange={setFighters} onSelectionChange={(selection) => setFighterSelection(normalizeFighterSelection(selection, fighters))} onClose={() => setDialog(null)} />}
      {dialog === "settings" && <SettingsDialog rules={rules} preferences={preferences} t={t} onChange={setRules} onPreferencesChange={setPreferences} onClose={() => setDialog(null)} />}
      {dialog === "abilities" && <AbilitiesDialog language={language} rules={rules} t={t} onChange={setRules} onClose={() => setDialog(null)} />}
      {dialog === "controls" && (
        <OriginalDialog title={t("controls.title")} onClose={() => setDialog(null)}>
          <div className="text-dialog">{(["controls.player1", "controls.player2", "controls.player3", "controls.player4"] as const).slice(0, rules.playerCount).map((key) => <p key={key}>{t(key)}</p>)}</div>
        </OriginalDialog>
      )}
      {dialog === "hall" && (
        <OriginalDialog title={t("hall.title")} onClose={() => setDialog(null)}>
          <ol className="hall-list">{[...fighters].sort((left, right) => right.score - left.score || left.name.localeCompare(right.name)).map((fighter) => <li key={fighter.id}><span>{fighter.name}</span><strong>{fighter.score}</strong></li>)}</ol>
        </OriginalDialog>
      )}
      {dialog === "authors" && (
        <OriginalDialog title={t("authors.title")} onClose={() => setDialog(null)}><p className="authors-text">{t("authors.text")}</p></OriginalDialog>
      )}
    </>
  );
};
